import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Dialog, Field, Input, toast } from '@/shared/ui';
import { DateField, TextareaField, TextField } from '@/shared/forms';
import { serviceEventTypeSelector } from '../../../components/vehicleCodebooks';
import { fileToBase64, MAX_ATTACHMENT_SIZE } from '../../internal/attachments';
import type {
  VehicleMaintenanceDocumentValue,
  VehicleMaintenanceFormValues,
} from '../vehicleMaintenanceFormSchema';
import { useVehicleMaintenanceForm } from '../useVehicleMaintenanceForm';

const ServiceTypeField = serviceEventTypeSelector.Field;

interface VehicleMaintenanceDialogProps {
  vehicleId: number;
  initialValues: VehicleMaintenanceFormValues;
  onClose: () => void;
}

/**
 * Dialog servisního záznamu a jeho příloh. Vybraný soubor se neuploaduje hned — jde do stavu
 * formuláře a odešle se až s `Uložit`, aby záznam i přílohy vznikly jednou transakcí.
 */
export function VehicleMaintenanceDialog({
  vehicleId,
  initialValues,
  onClose,
}: VehicleMaintenanceDialogProps) {
  const { t } = useTranslation('inventory');
  const [fileError, setFileError] = useState<string | null>(null);
  const isEdit = initialValues.id != null;

  const { form, submit, isSubmitting } = useVehicleMaintenanceForm({
    vehicleId,
    initialValues,
    onSaved: () => {
      onClose();
      toast({ severity: 'success', description: t('maintenance.toast.saved') });
    },
    onError: () =>
      toast({
        severity: 'error',
        title: t('maintenance.toast.errorTitle'),
        description: t('maintenance.toast.saveError'),
      }),
  });

  const addFiles = async (fileList: FileList | null) => {
    if (!fileList) return;
    const selected = Array.from(fileList);
    if (selected.some((file) => file.size > MAX_ATTACHMENT_SIZE)) {
      setFileError(t('maintenance.validation.fileTooLarge'));
      return;
    }

    const added: VehicleMaintenanceDocumentValue[] = await Promise.all(
      selected.map(async (file) => ({
        id: null,
        fileName: file.name,
        contentType: file.type || 'application/octet-stream',
        contentBase64: await fileToBase64(file),
      }))
    );

    setFileError(null);
    form.setFieldValue('documents', (current) => [...current, ...added]);
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={isEdit ? t('maintenance.dialog.editTitle') : t('maintenance.dialog.addTitle')}
      icon="wrench"
      width="lg"
      footer={
        <>
          <Button type="button" variant="outline" onClick={onClose}>
            {t('maintenance.cancel')}
          </Button>
          <form.Subscribe selector={(state) => state.canSubmit}>
            {(canSubmit) => (
              <Button
                type="button"
                variant="primary"
                loading={isSubmitting}
                disabled={!canSubmit}
                onClick={() => void submit()}
              >
                {t('maintenance.save')}
              </Button>
            )}
          </form.Subscribe>
        </>
      }
    >
      <div className="gap-phx-4 flex flex-col">
        <div className="gap-phx-4 grid grid-cols-1 md:grid-cols-2">
          <form.Field name="reference">
            {(field) => (
              <TextField field={field} label={t('maintenance.fields.id')} required maxLength={50} />
            )}
          </form.Field>

          <form.Field name="type">
            {(field) => (
              <ServiceTypeField
                field={field}
                mode="single"
                label={t('maintenance.fields.type')}
                required
                testId="maintenance-type"
              />
            )}
          </form.Field>

          <form.Field name="startDate">
            {(field) => (
              <DateField field={field} label={t('maintenance.fields.startDate')} required />
            )}
          </form.Field>

          <form.Field name="endDate">
            {(field) => (
              <DateField field={field} label={t('maintenance.fields.endDate')} required />
            )}
          </form.Field>
        </div>

        <form.Field name="description">
          {(field) => (
            <TextareaField
              field={field}
              layout="stacked"
              label={t('maintenance.fields.description')}
              required
              maxLength={500}
            />
          )}
        </form.Field>

        {/* Cena zůstává ruční — `TextField` jede přes `TextBox`, který `unit` nezná. */}
        <form.Field name="costPln">
          {(field) => (
            <Field
              label={t('maintenance.fields.cost')}
              required
              error={
                field.state.meta.isTouched
                  ? (field.state.meta.errors[0]?.message as string | undefined)
                  : undefined
              }
            >
              <Input
                required
                type="number"
                min={0}
                step="0.01"
                unit="PLN"
                value={field.state.value}
                onChange={(event) => field.handleChange(event.target.value)}
                onBlur={field.handleBlur}
              />
            </Field>
          )}
        </form.Field>

        <Field
          label={t('maintenance.fields.documents')}
          hint={t('maintenance.fields.documentsHint')}
          error={fileError ?? undefined}
        >
          <Input type="file" multiple onChange={(event) => void addFiles(event.target.files)} />
        </Field>

        <form.Field name="documents" mode="array">
          {(documentsField) =>
            documentsField.state.value.length === 0 ? null : (
              <div className="gap-phx-2 flex flex-col">
                {documentsField.state.value.map((document, index) => (
                  <div
                    key={document.id ?? `${document.fileName}-${index}`}
                    className="border-phx-border px-phx-3 py-phx-2 rounded-phx flex items-center justify-between border"
                  >
                    {document.id ? (
                      <a
                        className="text-phx-link text-phx-sm hover:underline"
                        href={`/api/inventory/vehicles/${vehicleId}/maintenance/${initialValues.id}/documents/${document.id}`}
                      >
                        {document.fileName}
                      </a>
                    ) : (
                      <span className="text-phx-sm">{document.fileName}</span>
                    )}
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      iconLeft="trash"
                      aria-label={t('maintenance.removeDocument')}
                      onClick={() => documentsField.removeValue(index)}
                    />
                  </div>
                ))}
              </div>
            )
          }
        </form.Field>
      </div>
    </Dialog>
  );
}
